import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  applyPagePartDirectoryChanges,
  createServerPagePartDocId,
  derivePageTree,
  isPageElementKind,
  PAGE_CONNECTOR_ENDS_ORIGIN,
  PAGE_CONNECTOR_KIND,
  PAGE_DOCUMENT_PART_ID,
  PAGE_LARGE_DATA_POLICY,
  PAGE_PART_DIRECTORY_ORIGIN,
  PAGE_TREE_DELETED_ID,
  pageBlockIdsOfKind,
  pageKindMissingPart,
  pagePartBlocksMap,
  type PagePartElement,
  pagePartElementsMap,
  pagePartEntryPlace,
  type PagePartRect,
  PagePartRectIndex,
  pagePartsMayHoldId,
  pagePartsWithKind,
  PageReadPartMissingError,
  type PageReadSource,
  pageReadSource,
  pageStructureAncestors,
  type PageStructureChange,
  type PageStructureDocs,
  type PageStructureEntry,
  type PageStructureIndex,
  pageStructureIndexOf,
  type PageStructureListener,
  pageStructureMap,
  PageStructureWriter,
  type PageTree,
  readPageConnectorEnds,
  readPagePartDirectory,
  samePagePartRect,
  samePagePartSummary,
  summarizePagePart,
} from '@streamlain/database-identity';
import * as Y from 'yjs';

export type {
  PageStructureChange,
  PageStructureListener,
} from '@streamlain/database-identity';

import type { YBlock } from '../block/types.js';
import { runSystemDocBarrier } from '../system-doc-barrier.js';
import {
  ensureWorkspaceDoc,
  type PagePartLoadReason,
  requestSystemDocLoad,
} from '../system-doc-loader.js';
import type { Store } from './store.js';

export type LoadedPagePart = {
  doc: Y.Doc;
  blocks: Y.Map<YBlock>;
  elements: Y.Map<PagePartElement>;
};

export type { PagePartRect };

/** True while the part has to stay in memory although it is out of sight. */
export type PagePartUnloadGuard = (
  partId: string,
  part: LoadedPagePart
) => boolean;

/** A loaded part out of sight frees its memory after this delay. */
export const PAGE_PART_UNLOAD_DELAY_MS = 30_000;

/** Changed parts write their bytes to the directory at most this often. */
export const PAGE_PART_BYTES_DELAY_MS = 1_000;

/**
 * The structure of one loaded page, shared by every store of the page: its
 * one index, the writer on it and the fields of its loaded parts.
 * Writes run inside the caller's store transaction.
 */
export class PageStructure {
  readonly index: PageStructureIndex;
  private readonly releaseIndex: () => void;

  readonly writer: PageStructureWriter;
  readonly docs: PageStructureDocs;
  private readonly deferredMeasures = new Map<
    string,
    { ids: Set<string>; local: boolean }
  >();
  private cachedDirectory?: ReturnType<typeof readPagePartDirectory>;
  private readonly releaseDirectory: () => void;
  private readonly directoryListeners = new Set<() => void>();

  private directory() {
    return (this.cachedDirectory ??= readPagePartDirectory(this.host.spaceDoc));
  }

  /**
   * Writes `changes` to the page directory. The cache goes at once: inside a
   * transaction the directory observer runs only at its end, and a planner
   * reading in between must see the bytes just measured.
   */
  private writeDirectory(
    changes: Parameters<typeof applyPagePartDirectoryChanges>[1]
  ): void {
    applyPagePartDirectoryChanges(this.host.spaceDoc, changes);
    this.cachedDirectory = undefined;
  }

  private partUnloaded(partId: string): boolean {
    return !this.parts.has(partId) || this.awaitingContent.has(partId);
  }
  private readonly pendingBytes = new Map<string, number>();
  // Changed fields of parts, announced when the head transaction ends.
  // Changed field ids of loaded parts, true when this device wrote them.
  private readonly deferredFields = new Map<string, boolean>();
  private readonly afterHead: (() => void)[] = [];
  private changeDepth = 0;

  /** Loaded parts changed since their bytes were last measured. */
  private readonly unmeasuredBytes = new Set<string>();

  private bytesTimer: ReturnType<typeof setTimeout> | undefined;

  /** The rectangle of each loaded part, kept per changed entry. */
  private readonly partRects = new Map<string, PagePartRectIndex>();
  private readonly parts = new Map<string, LoadedPagePart>();
  private readonly partDisposers = new Map<string, () => void>();
  private readonly loadDisposers = new Map<string, () => void>();
  private readonly partListeners = new Set<
    (part: LoadedPagePart, loaded: boolean, partId: string) => void
  >();
  private visibleRectParts: ReadonlySet<string> = new Set();
  private visibleFrontierParts: ReadonlySet<string> = new Set();
  private readonly unloaded: (id: string) => boolean;
  private visibleBlockIds: ReadonlySet<string> = new Set();
  private readonly unloadGuards = new Set<PagePartUnloadGuard>();
  private readonly unloadTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly contentLoads = new Map<string, Promise<void>>();
  private holders = 0;
  private disposed = false;

  get partIds(): readonly string[] {
    return [...this.directory().keys()];
  }

  /** The canvas rectangle of every part in the directory, loaded or not. */
  get partRectangles(): readonly PagePartRect[] {
    return [...this.directory().values()].flatMap(({ r }) => (r ? [r] : []));
  }

  blockFields(id: string): YBlock | undefined {
    const entry = this.entry(id);
    return entry && this.parts.get(entry.s)?.blocks.get(id);
  }

  elementFields(id: string): PagePartElement | undefined {
    const entry = this.entry(id);
    return entry && this.parts.get(entry.s)?.elements.get(id);
  }

  observeParts(
    listener: (part: LoadedPagePart, loaded: boolean, partId: string) => void
  ): () => void {
    this.partListeners.add(listener);
    for (const [partId, part] of this.parts) listener(part, true, partId);
    return () => {
      if (!this.partListeners.delete(listener)) return;
      for (const [partId, part] of this.parts) listener(part, false, partId);
    };
  }

  onPartTransaction(
    listener: (transaction: Y.Transaction, partId: string) => void
  ): () => void {
    const bindings = new Map<Y.Doc, (transaction: Y.Transaction) => void>();
    return this.observeParts((part, loaded, partId) => {
      if (!loaded) {
        const bound = bindings.get(part.doc);
        if (bound) part.doc.off('afterTransaction', bound);
        bindings.delete(part.doc);
        return;
      }
      const bound = (transaction: Y.Transaction) =>
        listener(transaction, partId);
      bindings.set(part.doc, bound);
      part.doc.on('afterTransaction', bound);
    });
  }

  loadPart(
    partId: string,
    reason: PagePartLoadReason = 'page-part-load'
  ): void {
    this.assertLive();
    if (this.parts.has(partId)) return;
    const docId = createServerPagePartDocId({
      workspaceId: this.host.workspace.id,
      pageId: this.host.doc.id,
      partId,
    });
    const doc = ensureWorkspaceDoc(this.host.workspace, docId);
    if (!doc)
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        `Page part document ${docId} could not be registered`
      );
    const part = {
      doc: doc.spaceDoc,
      blocks: doc.yBlocks,
      elements: pagePartElementsMap(doc.spaceDoc),
    };
    this.bindPart(partId, part);
    let release: (() => void) | undefined;
    try {
      release = requestSystemDocLoad(this.host, docId, {
        kind: 'page-part',
        workspaceId: this.host.workspace.id,
        pageId: this.host.doc.id,
        partId,
        reason,
      });
      doc.load();
      if (release) this.loadDisposers.set(partId, release);
    } catch (error) {
      this.unloadPart(partId);
      release?.();
      throw error;
    }
  }

  /** True when the fields of every entry `ids` not deleted are in memory. */
  fieldsLoaded(ids: readonly string[]): boolean {
    return !ids.some(id => this.lacksFields(id));
  }

  /**
   * Loads the parts of those `ids` that are entries of this page with their
   * fields out of memory; null when none is to load: loaded, deleted or no
   * entry of this page, such as an anchor of another page.
   */
  loadMissingPartsOf(ids: readonly string[]): Promise<void> | null {
    const candidates = this.partsForIds(ids);
    if (
      !candidates.size &&
      ids.every(id => !this.entry(id) || !this.lacksFields(id))
    )
      return null;
    return this.loadCandidates(candidates).then(() =>
      this.loadPartsOf(ids.filter(id => this.entry(id) !== undefined))
    );
  }

  /** Loads matching parts and the parent chains needed to decide placement. */
  async loadBlockIdsOfKind(
    kind: string,
    signal?: AbortSignal
  ): Promise<string[]> {
    await this.loadUntilPresent(() => {
      const parts = pagePartsWithKind(this.host.spaceDoc, kind).filter(partId =>
        this.partUnloaded(partId)
      );
      if (parts.length) return parts;
      const ancestor = pageKindMissingPart(this.readSource(), kind);
      return ancestor === undefined ? [] : [ancestor];
    }, signal);
    return pageBlockIdsOfKind(this.readSource(), kind);
  }

  /** Whether a lookup of the kind can be answered without loading parts. */
  kindLoaded(kind: string): boolean {
    return pageKindMissingPart(this.readSource(), kind) === undefined;
  }

  /**
   * Keeps what a lookup of the kind reads loaded while held: loads missing
   * parts now and after each change of the directory or the structure, and
   * reports each change of `kindLoaded`, the first one at once. A failed
   * load goes to `onError`. Returns the release.
   */
  holdKind(
    kind: string,
    onChange: (loaded: boolean) => void,
    onError: (error: unknown) => void
  ): () => void {
    this.assertLive();
    const controller = new AbortController();
    let loading = false;
    let reported: boolean | undefined;
    const check = () => {
      if (controller.signal.aborted) return;
      const loaded = this.kindLoaded(kind);
      if (loaded !== reported) {
        reported = loaded;
        onChange(loaded);
      }
      if (loaded || loading) return;
      loading = true;
      this.loadBlockIdsOfKind(kind, controller.signal).then(
        () => {
          loading = false;
          check();
        },
        (error: unknown) => {
          loading = false;
          if (!controller.signal.aborted) onError(error);
        }
      );
    };
    const releaseParts = this.retainKindParts(kind);
    const releaseStructure = this.subscribe(check);
    const releaseDirectory = this.subscribeDirectory(check);
    check();
    return () => {
      controller.abort();
      releaseStructure();
      releaseDirectory();
      releaseParts();
    };
  }

  // Calls the listener after each head transaction that changed the directory.
  private subscribeDirectory(listener: () => void): () => void {
    this.directoryListeners.add(listener);
    return () => {
      this.directoryListeners.delete(listener);
    };
  }

  unloadedPartsOf(id: string): string[] {
    if (this.entry(id)) return [];
    return pagePartsMayHoldId(this.directory(), id, partId =>
      this.partUnloaded(partId)
    );
  }

  generateBlockId(generate: () => string): string {
    let id = generate();
    while (this.unloadedPartsOf(id).length) {
      const directory = this.directory();
      if (this.unloadedPartsOf(id).some(partId => !directory.get(partId)?.m))
        this.assertBlockIdLoaded(id);
      id = generate();
    }
    return id;
  }

  assertBlockIdLoaded(id: string): void {
    const parts = this.unloadedPartsOf(id);
    if (parts.length)
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        `Page part ${parts.join(', ')} may hold block ${id}; load it first`
      );
  }

  private partsForIds(ids: readonly string[]): Set<string> {
    const result = new Set<string>();
    for (const id of ids) {
      const entry = this.entry(id);
      if (entry) {
        if (this.lacksFields(id)) result.add(entry.s);
        continue;
      }
      for (const partId of this.unloadedPartsOf(id)) result.add(partId);
    }
    return result;
  }

  /** Every round must make at least one previously missing part available. */
  private async loadUntilPresent(
    missing: () => readonly string[],
    signal?: AbortSignal
  ): Promise<void> {
    signal?.throwIfAborted();
    let parts = missing();
    while (parts.length) {
      await this.loadCandidates(new Set(parts), signal);
      signal?.throwIfAborted();
      const next = missing();
      const remaining = new Set(next);
      if (parts.every(partId => remaining.has(partId)))
        throw new PageReadPartMissingError(next[0]);
      parts = next;
    }
  }

  private async loadCandidates(
    partIds: ReadonlySet<string>,
    signal?: AbortSignal
  ): Promise<void> {
    await abortable(
      Promise.all([...partIds].map(partId => this.loadPartContent(partId))),
      signal
    );
  }

  /**
   * Loads the parts of the entries `ids` and resolves once the fields of
   * every one not deleted from the page are in memory. A part already
   * loading is awaited, not loaded again; a part that cannot be loaded, or
   * lacks a field after loading, rejects.
   */
  async loadPartsOf(
    ids: readonly string[],
    signal?: AbortSignal
  ): Promise<void> {
    this.assertLive();
    await this.loadCandidates(this.partsForIds(ids), signal);
    const missing = () => ids.filter(id => this.lacksFields(id));
    const lacking = missing();
    if (lacking.length)
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        `Page ${this.host.doc.id} lacks the fields of ${lacking.join(', ')} after loading their parts`
      );
  }

  /**
   * Loads every part of the page and keeps them in memory until the returned
   * release, for readers of the whole page such as exports. Rejects like
   * `loadPartsOf` when a part cannot be loaded; nothing stays held then.
   */
  async retainWholePage(signal?: AbortSignal): Promise<() => void> {
    this.assertLive();
    const release = this.retainGuarded(() => true);
    try {
      // A part keeps its place in the directory while it holds only deleted
      // entries; the whole page holds it too.
      await abortable(
        Promise.all(
          this.partIds
            .filter(partId => partId !== PAGE_DOCUMENT_PART_ID)
            .map(partId => this.loadPartContent(partId))
        ),
        signal
      );
      await this.loadPartsOf([...this.index.entries().keys()], signal);
    } catch (error) {
      release();
      throw error;
    }
    return release;
  }

  // Keeps the parts a lookup of the kind reads in memory: those whose
  // summary holds the kind and those holding an ancestor of a match.
  private retainKindParts(kind: string): () => void {
    return this.retainGuarded(partId => this.kindPartIds(kind).has(partId));
  }

  private kindPartIds(kind: string): ReadonlySet<string> {
    const parts = new Set(pagePartsWithKind(this.host.spaceDoc, kind));
    const entries = this.index.entries();
    for (const [id, entry] of entries) {
      if (entry.k !== kind) continue;
      for (const [, ancestor] of pageStructureAncestors(entries, id))
        if (ancestor) parts.add(ancestor.s);
    }
    return parts;
  }

  // A guard whose release lets parts out of sight unload after the usual delay.
  private retainGuarded(guard: PagePartUnloadGuard): () => void {
    const unguard = this.addUnloadGuard(guard);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      unguard();
      if (this.disposed) return;
      const visible = this.visiblePartIds();
      for (const partId of this.parts.keys())
        if (!visible.has(partId)) this.scheduleUnload(partId);
    };
  }

  // Deleted entries and those whose fields are in memory need no part.
  private lacksFields(id: string): boolean {
    const entry = this.entry(id);
    if (!entry)
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        `Page ${this.host.doc.id} has no entry ${id}`
      );
    if (entry.p === PAGE_TREE_DELETED_ID || entry.d !== undefined) return false;
    const fields = isPageElementKind(entry.k)
      ? this.elementFields(id)
      : this.blockFields(id);
    return fields === undefined;
  }

  // One content load per part, shared by every caller while it runs.
  private loadPartContent(partId: string): Promise<void> {
    clearTimeout(this.unloadTimers.get(partId));
    this.unloadTimers.delete(partId);
    const running = this.contentLoads.get(partId);
    if (running) return running;
    const load = (async () => {
      this.loadPart(partId);
      await runSystemDocBarrier(this.host, {
        rootDocId: this.host.doc.id,
        docIds: [
          createServerPagePartDocId({
            workspaceId: this.host.workspace.id,
            pageId: this.host.doc.id,
            partId,
          }),
        ],
        signal: new AbortController().signal,
      });
    })().finally(() => this.contentLoads.delete(partId));
    this.contentLoads.set(partId, load);
    return load;
  }

  unloadPart(partId: string): void {
    if (partId === PAGE_DOCUMENT_PART_ID) return;
    const part = this.parts.get(partId);
    if (!part) return;
    clearTimeout(this.unloadTimers.get(partId));
    this.unloadTimers.delete(partId);
    this.measureBytes([partId]);
    const ids = [...this.index.idsInPart(partId)];
    this.parts.delete(partId);
    this.index.unbindPart(partId);
    this.awaitingContent.delete(partId);
    this.pendingBytes.delete(partId);
    this.partRects.delete(partId);
    this.partDisposers.get(partId)?.();
    this.partDisposers.delete(partId);
    for (const listener of this.partListeners) listener(part, false, partId);
    this.index.fieldsChanged(ids, false);
    this.loadDisposers.get(partId)?.();
    this.loadDisposers.delete(partId);
  }

  /**
   * The canvas shows `rect`: parts whose directory rectangle lies within it
   * or the join distance around it load first.
   */
  setVisibleRect(rect: PagePartRect): void {
    if (this.disposed) return;
    const margin = PAGE_LARGE_DATA_POLICY.part.canvasJoinDistance;
    const [x, y, w, h] = rect;
    const parts = new Set<string>();
    for (const [partId, { r }] of this.directory()) {
      if (
        r &&
        r[0] <= x + w + margin &&
        r[0] + r[2] >= x - margin &&
        r[1] <= y + h + margin &&
        r[1] + r[3] >= y - margin
      )
        parts.add(partId);
    }
    this.visibleRectParts = parts;
    this.showVisibleParts();
  }

  /**
   * The page shows the blocks `ids`: their parts and those of their direct
   * siblings load first. Visible parents also load their next child frontier,
   * even when none of their children are loaded yet. Children without a Doc
   * view are skipped for visibility decisions, but retain no parts or siblings.
   */
  setVisibleBlocks(
    ids: readonly string[],
    visibleParents: readonly string[],
    viewlessIds: readonly string[]
  ): void {
    if (this.disposed) return;
    const tree = this.tree();
    const visible = new Set(ids);
    const viewless = new Set(viewlessIds);
    const parents = new Set<string>();
    const blockIds = new Set<string>();
    const add = (id: string | undefined) => {
      if (id !== undefined) blockIds.add(id);
    };
    for (const id of ids) {
      add(id);
      const parentId = tree.parentOf.get(id);
      if (parentId !== undefined) parents.add(parentId);
    }
    for (const parentId of parents) {
      const siblings = tree.children.get(parentId) ?? [];
      siblings.forEach((id, at) => {
        if (!visible.has(id)) return;
        add(siblings[at - 1]);
        add(siblings[at + 1]);
      });
    }
    const frontier = new Set<string>();
    const frontiers = new Set(visibleParents);
    for (const parent of visibleParents) add(parent);
    const directory = this.directory();
    for (const parentId of frontiers) {
      const children = tree.children.get(parentId) ?? [];
      const first = children[0];
      const last = children.at(-1);
      const viewedChildren = children.filter(id => !viewless.has(id));
      const firstViewed = viewedChildren[0];
      const lastViewed = viewedChildren.at(-1);
      const firstOrder = first && this.entry(first)?.o;
      const lastOrder = last && this.entry(last)?.o;
      let before: { id: string; order: string } | undefined;
      let after: { id: string; order: string } | undefined;
      for (const [partId, { m }] of directory) {
        if (this.parts.has(partId)) continue;
        const span = m?.children[parentId];
        if (!span) continue;
        if (
          (!last ||
            ((lastViewed === undefined || visible.has(lastViewed)) &&
              lastOrder !== undefined &&
              span[1] > lastOrder)) &&
          (!after || span[1] < after.order)
        )
          after = { id: partId, order: span[1] };
        if (
          first &&
          (firstViewed === undefined || visible.has(firstViewed)) &&
          firstOrder !== undefined &&
          span[2] < firstOrder &&
          (!before || span[2] > before.order)
        )
          before = { id: partId, order: span[2] };
      }
      if (before) frontier.add(before.id);
      if (after) frontier.add(after.id);
    }
    this.visibleFrontierParts = frontier;
    this.visibleBlockIds = blockIds;
    this.showVisibleParts();
  }

  /** A store of the page holds the structure; the last release disposes it. */
  retain(): () => void {
    this.assertLive();
    this.holders++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (--this.holders === 0) this.dispose();
    };
  }

  /**
   * The page has no store any more: unload timers, guards, part bindings and
   * load requests end. A later store of the page builds a new structure.
   */
  dispose(): void {
    if (this.disposed) return;
    this.measureBytes([...this.unmeasuredBytes]);
    clearTimeout(this.bytesTimer);
    this.disposed = true;
    for (const timer of this.unloadTimers.values()) clearTimeout(timer);
    this.unloadTimers.clear();
    this.unloadGuards.clear();
    this.contentLoads.clear();
    for (const listener of this.partListeners)
      for (const [partId, part] of this.parts) listener(part, false, partId);
    this.partListeners.clear();
    this.directoryListeners.clear();
    for (const dispose of this.partDisposers.values()) dispose();
    this.partDisposers.clear();
    for (const release of this.loadDisposers.values()) release();
    this.loadDisposers.clear();
    for (const partId of this.parts.keys())
      if (partId !== PAGE_DOCUMENT_PART_ID) this.index.unbindPart(partId);
    this.parts.clear();
    this.releaseIndex();
    this.releaseDirectory();
    this.visibleTree = undefined;
    this.pendingVisibility.clear();
    this.hiddenFields.clear();
    this.pendingBytes.clear();
    this.partRects.clear();
    this.visibleRectParts = new Set();
    this.visibleBlockIds = new Set();
    if (structures.get(this.host.spaceDoc) === this)
      structures.delete(this.host.spaceDoc);
  }

  private assertLive(): void {
    if (this.disposed)
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        `Page structure of ${this.host.doc.id} is disposed`
      );
  }

  /** Keeps parts in memory while `guard` holds them; returns the release. */
  addUnloadGuard(guard: PagePartUnloadGuard): () => void {
    this.unloadGuards.add(guard);
    return () => {
      this.unloadGuards.delete(guard);
    };
  }

  // The parts in sight, the parts of the groups in them with their members
  // and the parts of the connectors attached in them: groups and connectors
  // keep no canvas place, they lie where their members and ends lie.
  private visiblePartIds(): ReadonlySet<string> {
    const visible = new Set([
      ...this.visibleRectParts,
      ...this.visibleFrontierParts,
    ]);
    const entries = this.index.entries();
    const partOf = (id: string) => entries.get(id)?.s;
    for (const id of this.visibleBlockIds) {
      const partId = partOf(id);
      if (partId !== undefined) visible.add(partId);
      else
        for (const candidate of this.partsForIds([id])) visible.add(candidate);
    }
    // Relationships stored in loaded fields are known even when the
    // referenced entry itself still lives in an unloaded part.
    const known = new Set<string>();
    for (const partId of visible) {
      const part = this.parts.get(partId);
      if (!part) continue;
      for (const [id, fields] of part.elements) {
        if (fields.get('type') === 'connector')
          for (const end of readPageConnectorEnds(id, fields))
            if (end) known.add(end);
        for (const child of this.childIds(fields.get('children')))
          known.add(child);
      }
    }
    for (const id of known) {
      const part = partOf(id);
      if (part) visible.add(part);
      for (const candidate of this.unloadedPartsOf(id)) visible.add(candidate);
    }
    const memberParts = new Set<string>();
    for (const [groupId, members] of this.tree().members) {
      if (
        ![groupId, ...members].some(id => {
          const partId = partOf(id);
          return partId !== undefined && visible.has(partId);
        })
      )
        continue;
      for (const id of [groupId, ...members]) {
        const partId = partOf(id);
        if (partId !== undefined) memberParts.add(partId);
      }
    }
    for (const partId of visible)
      for (const id of this.index.idsInPart(partId))
        for (const connectorId of this.index.connectorsOf(id)) {
          const connectorPart = partOf(connectorId);
          if (connectorPart !== undefined) memberParts.add(connectorPart);
        }
    for (const partId of memberParts) visible.add(partId);
    return visible;
  }

  private showVisibleParts(): void {
    this.assertLive();
    let visible = this.visiblePartIds();
    for (;;) {
      let loaded = false;
      for (const partId of visible) {
        clearTimeout(this.unloadTimers.get(partId));
        this.unloadTimers.delete(partId);
        if (!this.parts.has(partId)) {
          this.loadPart(partId, 'viewport');
          loaded = true;
        }
      }
      if (!loaded) break;
      visible = this.visiblePartIds();
    }
    for (const partId of this.parts.keys()) {
      if (!visible.has(partId)) this.scheduleUnload(partId);
    }
  }

  private scheduleUnload(partId: string): void {
    if (partId === PAGE_DOCUMENT_PART_ID || this.unloadTimers.has(partId))
      return;
    this.unloadTimers.set(
      partId,
      setTimeout(() => {
        this.unloadTimers.delete(partId);
        this.unloadIdlePart(partId);
      }, PAGE_PART_UNLOAD_DELAY_MS)
    );
  }

  // Only memory is freed: the part document and its local data stay.
  private unloadIdlePart(partId: string): void {
    const part = this.parts.get(partId);
    if (!part || this.visiblePartIds().has(partId)) return;
    if ([...this.unloadGuards].some(guard => guard(partId, part))) {
      this.scheduleUnload(partId);
      return;
    }
    this.unloadPart(partId);
  }

  /** The encoded snapshot size used by the part directory and insertion checks. */
  measureSnapshotBytes(doc: Y.Doc): number {
    return Y.encodeStateAsUpdate(doc).byteLength;
  }

  /** Stored snapshot bytes plus writes made in the current part transaction. */
  planningDirectory(persist = true) {
    if (persist) this.measureBytes([...this.unmeasuredBytes]);
    const directory = new Map(this.directory());
    if (!persist) {
      for (const id of this.unmeasuredBytes) {
        const part = this.parts.get(id);
        if (part)
          directory.set(id, {
            ...directory.get(id),
            b: this.measureSnapshotBytes(part.doc),
          });
      }
    }
    for (const [partId, pending] of this.pendingBytes) {
      const previous = directory.get(partId);
      directory.set(partId, { ...previous, b: (previous?.b ?? 0) + pending });
    }
    return directory;
  }

  recordPartDelta(partId: string, before: Uint8Array): void {
    const part = this.parts.get(partId);
    if (!part) return;
    const delta = Y.encodeStateAsUpdate(part.doc, before).byteLength;
    this.pendingBytes.set(partId, (this.pendingBytes.get(partId) ?? 0) + delta);
  }

  /** Measures part `partId` after its entries `ids` changed. */
  private queuePartMeasure(
    partId: string,
    ids: Iterable<string>,
    local = true
  ): void {
    if (
      this.changeDepth > 0 ||
      this.host.spaceDoc._transaction ||
      this.host.spaceDoc._transactionCleanups.length
    ) {
      let pending = this.deferredMeasures.get(partId);
      if (!pending) {
        pending = { ids: new Set(), local };
        this.deferredMeasures.set(partId, pending);
        if (this.changeDepth === 0)
          this.host.spaceDoc.once('afterAllTransactions', () =>
            this.flushMeasures()
          );
      }
      pending.local ||= local;
      for (const id of ids) pending.ids.add(id);
      return;
    }
    this.measurePart(partId, ids, local);
  }

  private flushMeasures(): void {
    if (this.disposed) return;
    const pending = [...this.deferredMeasures];
    this.deferredMeasures.clear();
    for (const [partId, { ids, local }] of pending)
      this.queuePartMeasure(partId, ids, local);
  }

  private measurePart(
    partId: string,
    ids: Iterable<string>,
    local: boolean
  ): void {
    if (local) this.pendingBytes.delete(partId);
    const part = this.parts.get(partId);
    const rects = this.partRects.get(partId);
    if (!part || !rects) return;
    const entries = this.index.entries();
    for (const id of ids)
      rects.set(
        id,
        pagePartEntryPlace(
          partId,
          id,
          part.blocks.get(id) ?? part.elements.get(id),
          entries
        )
      );
    const previous = this.directory().get(partId);
    const r = rects.rect();
    const m = summarizePagePart(
      new Map(
        [...this.index.idsInPart(partId)].map(id => [id, entries.get(id)!])
      )
    );
    if (!local) {
      // Summaries describe merged entries; bytes and rectangles remain local.
      if (previous && (!previous.m || !samePagePartSummary(previous.m, m)))
        this.writeDirectory([
          { kind: 'set', partId, value: { ...previous, m } },
        ]);
      return;
    }
    if (previous === undefined) {
      // A new part enters the directory with its measured bytes.
      const b = this.measureSnapshotBytes(part.doc);
      this.writeDirectory([
        { kind: 'set', partId, value: { b, ...(r ? { r } : {}), m } },
      ]);
      return;
    }
    this.unmeasuredBytes.add(partId);
    this.bytesTimer ??= setTimeout(() => {
      this.bytesTimer = undefined;
      this.measureBytes([...this.unmeasuredBytes]);
    }, PAGE_PART_BYTES_DELAY_MS);
    if (
      samePagePartRect(previous.r, r) &&
      previous.m !== undefined &&
      samePagePartSummary(previous.m, m)
    )
      return;
    const { r: _previous, ...rest } = previous;
    this.writeDirectory([
      { kind: 'set', partId, value: { ...rest, ...(r ? { r } : {}), m } },
    ]);
  }

  /**
   * Writes the bytes of the changed parts among `partIds` to the directory:
   * measured once when they are needed, not per transaction.
   */
  private measureBytes(partIds: readonly string[]): void {
    const changes = [];
    const directory = this.directory();
    for (const partId of partIds) {
      if (!this.unmeasuredBytes.delete(partId)) continue;
      const part = this.parts.get(partId);
      const previous = directory.get(partId);
      if (!part || !previous) continue;
      const b = this.measureSnapshotBytes(part.doc);
      if (previous.b !== b)
        changes.push({
          kind: 'set' as const,
          partId,
          value: { ...previous, b },
        });
    }
    if (changes.length) this.writeDirectory(changes);
  }

  /**
   * Runs `fn` now or, while a transaction of the head runs, once it ends.
   * A part created in a store transaction is outside of it: each write to
   * the part ends a transaction of its own, whose observers run at once.
   * Deferred, they see the page once, as for the parts the store
   * transaction spans.
   */
  afterHeadTransaction(fn: () => void): void {
    const head = this.host.spaceDoc;
    if (this.changeDepth > 0) {
      this.afterHead.push(fn);
      return;
    }
    if (!head._transaction) {
      fn();
      return;
    }
    if (this.afterHead.length === 0)
      head.once('afterTransaction', () => this.flushChanges());
    this.afterHead.push(fn);
  }

  private flushChanges(): void {
    const deferred = this.afterHead.splice(0);
    if (!this.disposed) for (const run of deferred) run();
  }

  /**
   * Shares the store's deferred observers and measurements across a step.
   * Undo managers retain their own Yjs transactions and origins: wrapping
   * them in a Yjs transaction would prevent their redo items being captured.
   */
  batchChanges(write: () => void): void {
    this.changeDepth++;
    try {
      write();
    } finally {
      if (--this.changeDepth === 0) {
        if (this.host.spaceDoc._transaction)
          this.host.spaceDoc.once('afterTransaction', () =>
            this.flushChanges()
          );
        else this.flushChanges();
        if (
          this.host.spaceDoc._transaction ||
          this.host.spaceDoc._transactionCleanups.length
        )
          this.host.spaceDoc.once('afterAllTransactions', () =>
            this.flushMeasures()
          );
        else this.flushMeasures();
      }
    }
  }

  /** Announces changed fields of loaded parts, once per head transaction. */
  private fieldsChanged(ids: Iterable<string>, local: boolean): void {
    const first = this.deferredFields.size === 0;
    for (const id of ids)
      this.deferredFields.set(
        id,
        local || this.deferredFields.get(id) === true
      );
    if (first)
      this.afterHeadTransaction(() => {
        const changed = [...this.deferredFields];
        this.deferredFields.clear();
        for (const local of [true, false]) {
          const ids = changed.filter(([, own]) => own === local);
          if (ids.length)
            this.index.fieldsChanged(
              ids.map(([id]) => id),
              local
            );
        }
      });
  }

  private bindPart(partId: string, part: LoadedPagePart): void {
    this.parts.set(partId, part);
    this.index.bindPart(partId, pageStructureMap(part.doc));
    // A part bound before its content arrived checks its entries with the
    // first transaction of that content: loading it does not hide and show
    // again every entry it holds.
    const recheck = () => {
      for (const id of this.index.idsInPart(partId))
        this.pendingVisibility.add(id);
    };
    if (part.blocks.size > 0 || part.elements.size > 0) recheck();
    else this.awaitingContent.add(partId);
    const rects = new PagePartRectIndex();
    const entries = this.index.entries();
    for (const map of [part.blocks, part.elements])
      for (const [id, fields] of map)
        rects.set(id, pagePartEntryPlace(partId, id, fields, entries));
    this.partRects.set(partId, rects);
    // Both field maps report the same keys: one observer for either.
    const observer = (event: {
      keysChanged: Set<string>;
      transaction: Y.Transaction;
    }) => {
      this.fieldsChanged(event.keysChanged, event.transaction.local);
    };
    // The entries a transaction changed: fields added or removed, a direct
    // field such as xywh, or in part zero an entry of the head. Local ones
    // are measured; remote ones may have overwritten connector ends.
    const changed = (transaction: Y.Transaction) => {
      if (
        transaction.origin === PAGE_PART_DIRECTORY_ORIGIN ||
        transaction.changed.size === 0
      )
        return;
      if (this.awaitingContent.delete(partId)) recheck();
      const ids = new Set<string>();
      const fieldMaps = new Set<unknown>([
        part.blocks,
        part.elements,
        pageStructureMap(part.doc),
      ]);
      for (const [type, keys] of transaction.changed) {
        if (fieldMaps.has(type)) {
          for (const key of keys) if (key !== null) ids.add(key);
          continue;
        }
        const id = type._item?.parentSub;
        if (
          id &&
          (type.parent === part.blocks || type.parent === part.elements)
        )
          ids.add(id);
      }
      this.queuePartMeasure(partId, ids, transaction.local);
      if (!transaction.local) this.repairConnectorEnds(ids);
      // Wait for the head's field-removal observers before binding the new
      // parts, including parts whose content is already present.
      // Keyed by the changed shared types, whatever their event type.
      const changedTypes: ReadonlyMap<
        unknown,
        Set<string | null>
      > = transaction.changed;
      if (
        !transaction.local &&
        [...(changedTypes.get(pageStructureMap(part.doc)) ?? [])].some(
          id => id !== null && this.visibleBlockIds.has(id)
        )
      )
        this.afterHeadTransaction(() => this.showVisibleParts());
    };
    part.doc.on('afterTransaction', changed);
    part.blocks.observe(observer);
    part.elements.observe(observer);
    this.partDisposers.set(partId, () => {
      part.doc.off('afterTransaction', changed);
      part.blocks.unobserve(observer);
      part.elements.unobserve(observer);
    });
    for (const listener of this.partListeners) listener(part, true, partId);
    this.index.fieldsChanged(
      [...part.blocks.keys(), ...part.elements.keys()],
      false
    );
    this.repairConnectorEnds(part.elements.keys());
  }

  /**
   * Repeats the ends of the loaded live connectors among `ids` from their
   * fields: the head keeps each entry whole, so a concurrent write of a
   * connector entry, such as a join, leaves the ends it read.
   */
  private repairConnectorEnds(ids: Iterable<string>): void {
    const connectors = [...ids].filter(id => {
      const entry = this.entry(id);
      return (
        entry?.k === PAGE_CONNECTOR_KIND &&
        entry.p !== PAGE_TREE_DELETED_ID &&
        this.elementFields(id) !== undefined
      );
    });
    if (connectors.length === 0) return;
    this.host.spaceDoc.transact(() => {
      for (const id of connectors) this.writer.syncConnectorEnds(id);
    }, PAGE_CONNECTOR_ENDS_ORIGIN);
  }

  /** Fields of the canvas elements by id. */
  readonly elements: Y.Map<PagePartElement>;

  // Members named before they are placed, as imports create a frame before
  // the notes in it: they join when they are placed.
  private readonly pendingGroups = new Map<string, string>();

  constructor(
    private readonly host: Pick<Store, 'spaceDoc' | 'doc' | 'workspace'>
  ) {
    const doc = host.spaceDoc;
    const directory = doc.getMap('pageParts');
    const directoryChanged = (event: Y.YMapEvent<unknown>) => {
      this.cachedDirectory = undefined;
      if (this.directoryListeners.size)
        this.afterHeadTransaction(() => {
          for (const listener of this.directoryListeners) listener();
        });
      if (!event.transaction.local) {
        const ids = [...event.keysChanged];
        doc.once('afterAllTransactions', () => {
          if (this.disposed) return;
          for (const id of ids)
            if (!this.partUnloaded(id)) this.queuePartMeasure(id, [], false);
        });
      }
    };
    directory.observe(directoryChanged);
    this.releaseDirectory = () => directory.unobserve(directoryChanged);
    this.docs = {
      loadedPartIds: () => [...this.parts.keys()],
      head: doc,
      partStructure: partId => {
        const part = this.parts.get(partId);
        return part ? pageStructureMap(part.doc) : null;
      },
      partBlocks: partId => {
        const part = this.parts.get(partId);
        return part ? pagePartBlocksMap(part.doc) : null;
      },
      partElements: partId => this.parts.get(partId)?.elements ?? null,
      createPart: partId => {
        if (partId === PAGE_DOCUMENT_PART_ID) return;
        this.loadPart(partId);
        if (!this.directory().has(partId)) {
          this.writeDirectory([
            {
              kind: 'set',
              partId,
              value: { b: 0, m: summarizePagePart(new Map()) },
            },
          ]);
        }
      },
    };
    let rule = unloadedRules.get(doc);
    if (!rule) {
      rule = {
        current: this,
        unloaded: id => {
          const current = rule!.current;
          return (
            pagePartsMayHoldId(current.directory(), id, partId =>
              current.partUnloaded(partId)
            ).length > 0
          );
        },
      };
      unloadedRules.set(doc, rule);
    }
    rule.current = this;
    this.unloaded = rule.unloaded;
    this.index = pageStructureIndexOf(pageStructureMap(doc), {
      unloaded: this.unloaded,
    });
    // Deleting or restoring an entry writes the head only: the rectangle of
    // its loaded part is measured again. Part zero measures with the head.
    // A write changes whether its own entry is visible, no other: the tree
    // checks only written entries.
    const releaseWrites = this.index.observeWrites(id =>
      this.pendingVisibility.add(id)
    );
    const releaseChanges = this.index.subscribe(change => {
      // Loaded fields that appeared or went away (fieldsChanged).
      for (const id of change.ids) this.pendingVisibility.add(id);
      const entries = this.index.entries();
      const parts = new Map<string, string[]>();
      for (const id of change.ids) {
        const partId = entries.get(id)?.s;
        if (
          partId === undefined ||
          partId === PAGE_DOCUMENT_PART_ID ||
          !this.parts.has(partId)
        )
          continue;
        const ids = parts.get(partId);
        if (ids) ids.push(id);
        else parts.set(partId, [id]);
      }
      for (const [partId, ids] of parts)
        this.queuePartMeasure(partId, ids, change.local);
    });
    this.releaseIndex = () => {
      releaseWrites();
      releaseChanges();
    };
    this.writer = new PageStructureWriter(this.docs, this.index);
    this.elements = pagePartElementsMap(doc);
    this.bindPart(PAGE_DOCUMENT_PART_ID, {
      doc,
      blocks: host.doc.yBlocks,
      elements: this.elements,
    });
  }

  private visibleTree?: { revision: number; tree: PageTree };
  private readonly pendingVisibility = new Set<string>();
  private readonly hiddenFields = new Set<string>();
  // Bound parts whose content has not arrived yet.
  private readonly awaitingContent = new Set<string>();

  tree(): PageTree {
    const source = this.index.tree();
    const { revision } = this.index;
    const sourceChanged = this.visibleTree?.revision !== revision;
    const entries = this.index.entries();
    // Only written entries and those of a bound part change their
    // visibility; both are pending.
    let visibilityChanged = false;
    for (const id of this.pendingVisibility) {
      const entry = entries.get(id);
      const hidden = entry !== undefined && this.fieldsMissing(id, entry);
      if (hidden === this.hiddenFields.has(id)) continue;
      visibilityChanged = true;
      if (hidden) this.hiddenFields.add(id);
      else this.hiddenFields.delete(id);
    }
    this.pendingVisibility.clear();
    if (sourceChanged || visibilityChanged) {
      const tree = this.hiddenFields.size
        ? derivePageTree(
            entries,
            id => this.hiddenFields.has(id),
            this.unloaded
          )
        : source;
      this.visibleTree = { revision, tree };
    }
    return this.visibleTree!.tree;
  }

  private fieldsMissing(id: string, entry: PageStructureEntry): boolean {
    const part = isPageElementKind(entry.k)
      ? this.docs.partElements(entry.s)
      : this.docs.partBlocks(entry.s);
    return part !== null && !part.has(id);
  }

  entry(id: string): PageStructureEntry | undefined {
    return this.index.entries().get(id);
  }

  /** Members of a group or frame, by (o, id). */
  members(groupId: string): readonly string[] {
    return this.tree().members.get(groupId) ?? [];
  }

  /**
   * The origin of a write to part document `doc` now: the one its history
   * tracks inside a tracked store transaction, none outside of one.
   */
  partWriteOrigin(doc: Y.Doc): number | null {
    const head = this.host.spaceDoc;
    return head._transaction?.origin === head.clientID ? doc.clientID : null;
  }

  /**
   * Runs `fn` in one transaction of every loaded part with its write origin,
   * so the writes of a store transaction are one step in every part.
   */
  transactParts(fn: () => void): void {
    const docs = [...this.parts.values()]
      .map(part => part.doc)
      .filter(doc => doc !== this.host.spaceDoc);
    const run = (index: number): void => {
      const doc = docs[index];
      if (doc === undefined) fn();
      else doc.transact(() => run(index + 1), this.partWriteOrigin(doc));
    };
    this.batchChanges(() => run(0));
  }

  /** Reads this page over the parts loaded now; another part throws. */
  readSource(): PageReadSource {
    return pageReadSource(
      this.host.spaceDoc,
      new Map([...this.parts].map(([partId, part]) => [partId, part.doc]))
    );
  }

  /** Live connectors attached to `id`, from loaded parts. */
  connectorsOf(id: string): ReadonlySet<string> {
    return this.index.connectorsOf(id);
  }

  /** Unloaded parts that can contain connectors attached to these ends. */
  missingConnectorParts(ids: readonly string[]): string[] {
    const ends = new Set(ids);
    return [...this.directory()]
      .filter(
        ([partId, { m }]) =>
          partId !== PAGE_DOCUMENT_PART_ID &&
          this.partUnloaded(partId) &&
          (!m || m.ends?.some(id => ends.has(id)))
      )
      .map(([partId]) => partId);
  }

  loadConnectorParts(
    ids: readonly string[],
    signal?: AbortSignal
  ): Promise<void> | null {
    if (this.missingConnectorParts(ids).length === 0) return null;
    return this.loadUntilPresent(() => this.missingConnectorParts(ids), signal);
  }

  /** The members of `groupId` and, in nested groups, theirs, from the head. */
  descendantMembers(groupId: string): string[] {
    const { members } = this.tree();
    const seen = new Set([groupId]);
    const result: string[] = [];
    const visit = (id: string) => {
      for (const member of members.get(id) ?? []) {
        if (seen.has(member)) continue;
        seen.add(member);
        result.push(member);
        visit(member);
      }
    };
    visit(groupId);
    return result;
  }

  private childIds(children: unknown): string[] {
    return children instanceof Y.Map
      ? [...children.keys()]
      : children && typeof children === 'object' && !Array.isArray(children)
        ? Object.keys(children)
        : [];
  }

  missingMemberParts(groupId: string): string[] {
    const groups = new Set([groupId, ...this.descendantMembers(groupId)]);
    const parts = new Set<string>();
    const directory = this.directory();
    for (const id of groups) {
      for (const [partId, { m }] of directory)
        if (
          partId !== PAGE_DOCUMENT_PART_ID &&
          this.partUnloaded(partId) &&
          (!m || m.groups?.includes(id))
        )
          parts.add(partId);
      const fields = this.elementFields(id);
      const ids = this.childIds(fields?.get('children'));
      for (const child of ids)
        for (const partId of this.unloadedPartsOf(child)) parts.add(partId);
    }
    return [...parts];
  }

  loadMemberParts(
    groupIds: readonly string[],
    signal?: AbortSignal
  ): Promise<void> | null {
    const missing = () => [
      ...new Set(groupIds.flatMap(id => this.missingMemberParts(id))),
    ];
    if (missing().length === 0) return null;
    return (async () => {
      await this.loadUntilPresent(missing, signal);
      await this.loadPartsOf(
        groupIds.flatMap(id => this.descendantMembers(id)),
        signal
      );
    })();
  }

  /** The group or frame a block or element belongs to. */
  groupOf(id: string): string | undefined {
    return this.tree().groupOf.get(id);
  }

  subscribe(listener: PageStructureListener): () => void {
    let active = true;
    let pending: PageStructureChange | undefined;
    const release = this.index.subscribe(change => {
      if (pending) {
        for (const key of [
          'ids',
          'parents',
          'groups',
          'reordered',
          'presented',
        ] as const)
          for (const id of change[key]) (pending[key] as Set<string>).add(id);
        pending.local ||= change.local;
        return;
      }
      pending = {
        ids: new Set(change.ids),
        parents: new Set(change.parents),
        groups: new Set(change.groups),
        reordered: new Set(change.reordered),
        presented: new Set(change.presented),
        local: change.local,
      };
      this.afterHeadTransaction(() => {
        const ready = pending!;
        pending = undefined;
        if (active) listener(ready);
      });
    });
    return () => {
      active = false;
      release();
    };
  }

  /** Makes a block or element a member of a group or frame. */
  join(memberId: string, groupId: string): void {
    if (this.entry(memberId) === undefined) {
      this.pendingGroups.set(memberId, groupId);
      return;
    }
    this.pendingGroups.delete(memberId);
    if (this.entry(memberId)!.g !== groupId) {
      this.writer.setGroup(memberId, groupId);
    }
  }

  /** Takes a block or element out of its group or frame. */
  leave(memberId: string): void {
    this.pendingGroups.delete(memberId);
    if (this.entry(memberId)?.g !== undefined) {
      this.writer.setGroup(memberId, undefined);
    }
  }

  /** A block or element was placed: it joins the group named for it. */
  placed(id: string): void {
    const groupId = this.pendingGroups.get(id);
    if (groupId === undefined) return;
    this.pendingGroups.delete(id);
    if (this.entry(groupId) !== undefined) this.writer.setGroup(id, groupId);
  }
}

function abortable<T>(work: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return work;
  signal.throwIfAborted();
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason);
    signal.addEventListener('abort', abort, { once: true });
    work
      .then(resolve, reject)
      .finally(() => signal.removeEventListener('abort', abort));
  });
}

// One per page document.
const structures = new WeakMap<Y.Doc, PageStructure>();

export function pageStructureOf(store: Store): PageStructure {
  const doc = store.spaceDoc;
  let structure = structures.get(doc);
  if (!structure) {
    structure = new PageStructure(store);
    structures.set(doc, structure);
  }
  return structure;
}

// The shared index outlives a Store; retain its unloaded-rule identity.
const unloadedRules = new WeakMap<
  Y.Doc,
  {
    current: PageStructure;
    unloaded: (id: string) => boolean;
  }
>();
